import { env } from '@/lib/utils/env';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import cookies from 'js-cookie';
import {
  PatientDto,
  PatientOverviewDto,
  VideoCallAppointmentDto,
  VideoCallJoinDto,
} from '@/lib/types';

import { ApiTag } from '../api-tags.enum';
import { HttpMethod } from '../http-method.enum';
import {
  ChangePatientProgramResultDto,
  CurrentProgramDto,
  ProgramCategory,
  ProgramDayContentArgs,
  ProgramDayContentDto,
} from './program.types';

export const patientsApi = createApi({
  reducerPath: 'patientsApi',
  tagTypes: [
    ApiTag.Patient,
    ApiTag.VideoCallAppointment,
    ApiTag.PatientOverview,
  ],
  refetchOnReconnect: true,
  refetchOnFocus: true,
  baseQuery: fetchBaseQuery({
    baseUrl: env.BASE_URL,
    credentials: 'include',
    prepareHeaders: (headers) => {
      const accessToken = cookies.get('access_token');
      if (accessToken) {
        headers.set('Authorization', `Bearer ${accessToken}`);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getAllPatients: builder.query<PatientDto[], void>({
      query: () => ({
        url: `/specialists/patients/list`,
        method: HttpMethod.Get,
      }),
      providesTags: (result) =>
        result
          ? [
              ApiTag.Patient,
              ...result.map(({ id }) => ({ type: ApiTag.Patient as const, id })),
            ]
          : [ApiTag.Patient],
    }),
    getPatientOverview: builder.query<
      PatientOverviewDto,
      { patientId: string; from: string; to: string; feedbackLimit?: number }
    >({
      query: ({ patientId, from, to, feedbackLimit }) => ({
        url: `/specialists/patients/${patientId}/overview`,
        method: HttpMethod.Get,
        params: { from, to, feedbackLimit },
      }),
      providesTags: (_result, _error, { patientId }) => [
        { type: ApiTag.PatientOverview, id: patientId },
      ],
    }),
    getVideoCallAppointments: builder.query<VideoCallAppointmentDto[], string>({
      query: (patientId) => ({
        url: `/specialists/patients/${patientId}/video-call-appointments`,
        method: HttpMethod.Get,
      }),
      providesTags: (_result, _error, patientId) => [
        { type: ApiTag.VideoCallAppointment, id: patientId },
      ],
    }),
    createVideoCallAppointment: builder.mutation<
      VideoCallAppointmentDto,
      { patientId: string; scheduledAt: string; note?: string; durationMinutes?: number }
    >({
      query: ({ patientId, ...body }) => ({
        url: `/specialists/patients/${patientId}/video-call-appointments`,
        method: HttpMethod.Post,
        body,
      }),
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: ApiTag.VideoCallAppointment, id: patientId },
        { type: ApiTag.VideoCallAppointment, id: 'RANGE' },
      ],
    }),
    updateVideoCallAppointment: builder.mutation<
      VideoCallAppointmentDto,
      {
        patientId: string;
        appointmentId: string;
        scheduledAt?: string;
        note?: string;
        durationMinutes?: number;
      }
    >({
      query: ({ appointmentId, patientId: _patientId, ...body }) => ({
        url: `/specialists/video-call-appointments/${appointmentId}`,
        method: HttpMethod.Patch,
        body,
      }),
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: ApiTag.VideoCallAppointment, id: patientId },
        { type: ApiTag.VideoCallAppointment, id: 'RANGE' },
      ],
    }),
    getSpecialistVideoCallAppointments: builder.query<
      VideoCallAppointmentDto[],
      { from: string; to: string }
    >({
      query: ({ from, to }) => ({
        url: `/specialists/video-call-appointments?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
        method: HttpMethod.Get,
      }),
      providesTags: [{ type: ApiTag.VideoCallAppointment, id: 'RANGE' }],
    }),
    cancelVideoCallAppointment: builder.mutation<
      VideoCallAppointmentDto,
      { patientId: string; appointmentId: string }
    >({
      query: ({ appointmentId }) => ({
        url: `/specialists/video-call-appointments/${appointmentId}/cancel`,
        method: HttpMethod.Post,
      }),
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: ApiTag.VideoCallAppointment, id: patientId },
        { type: ApiTag.VideoCallAppointment, id: 'RANGE' },
      ],
    }),
    joinVideoCallAppointment: builder.mutation<
      VideoCallJoinDto,
      { appointmentId: string }
    >({
      query: ({ appointmentId }) => ({
        url: `/specialists/video-call-appointments/${appointmentId}/join`,
        method: HttpMethod.Post,
      }),
    }),
    getCurrentProgram: builder.query<CurrentProgramDto, string>({
      query: (patientId) => ({
        url: `/user-exercises/${patientId}/current-program`,
        method: HttpMethod.Get,
      }),
      transformResponse: (response: {
        statusCode: number;
        data: CurrentProgramDto;
      }) => response.data,
      providesTags: (_result, _error, patientId) => [
        { type: ApiTag.Patient, id: `${patientId}-program` },
      ],
    }),
    changePatientProgram: builder.mutation<
      ChangePatientProgramResultDto,
      { patientId: string; category: ProgramCategory; level: number }
    >({
      query: ({ patientId, category, level }) => ({
        url: `/user-exercises/${patientId}/personalized-program`,
        method: HttpMethod.Patch,
        body: { category, level },
      }),
      transformResponse: (response: {
        statusCode: number;
        message: string;
        data: ChangePatientProgramResultDto;
      }) => response.data,
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: ApiTag.Patient, id: patientId },
        { type: ApiTag.Patient, id: `${patientId}-program` },
        { type: ApiTag.Patient, id: `${patientId}-ai-day` },
        { type: ApiTag.Patient, id: `${patientId}-exercise-content` },
      ],
    }),
    getPatientExerciseContent: builder.query<
      ProgramDayContentDto | null,
      ProgramDayContentArgs
    >({
      query: ({ patientId, category, level, day }) => {
        const params = new URLSearchParams();
        if (category) params.set('category', category);
        if (level !== undefined) params.set('level', String(level));
        if (day !== undefined) params.set('day', String(day));
        const qs = params.toString();
        return {
          url: `/user-exercises/${patientId}/exercise-content${qs ? `?${qs}` : ''}`,
          method: HttpMethod.Get,
        };
      },
      transformResponse: (response: {
        statusCode: number;
        data: ProgramDayContentDto | null;
      }) => response.data,
      providesTags: (_result, _error, { patientId }) => [
        { type: ApiTag.Patient, id: `${patientId}-exercise-content` },
      ],
    }),
    getPatientAiDay: builder.query<
      | {
          id: string;
          day: number;
          status: string;
          completed_at: string | null;
          ai_explanation: string | null;
          poc_exercise_groups: any[];
        }
      | null,
      string
    >({
      query: (patientId) => ({
        url: `/user-exercises/${patientId}/ai-day`,
        method: HttpMethod.Get,
      }),
      transformResponse: (response: {
        statusCode: number;
        data: any;
      }) => response.data,
      providesTags: (_result, _error, patientId) => [
        { type: ApiTag.Patient, id: `${patientId}-ai-day` },
      ],
    }),
    generatePatientAiDay: builder.mutation<
      {
        statusCode: number;
        data: {
          day: {
            id: string;
            day: number;
            status: string;
            completed_at: string | null;
            ai_explanation: string | null;
            poc_exercise_groups: any[];
          };
        };
      },
      { patientId: string }
    >({
      query: ({ patientId }) => ({
        url: `/user-exercises/${patientId}/ai-day/generate`,
        method: HttpMethod.Patch,
      }),
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: ApiTag.Patient, id: `${patientId}-ai-day` },
      ],
    }),
    regeneratePatientAiDay: builder.mutation<
      {
        statusCode: number;
        data: {
          day: {
            id: string;
            day: number;
            status: string;
            completed_at: string | null;
            ai_explanation: string | null;
            poc_exercise_groups: any[];
          };
        };
      },
      { patientId: string; prompt?: string }
    >({
      query: ({ patientId, prompt }) => ({
        url: `/user-exercises/${patientId}/ai-day/regenerate`,
        method: HttpMethod.Patch,
        body: prompt ? { prompt } : {},
      }),
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: ApiTag.Patient, id: `${patientId}-ai-day` },
      ],
    }),
  }),
});

export const {
  useGetAllPatientsQuery,
  useGetPatientOverviewQuery,
  useGetVideoCallAppointmentsQuery,
  useCreateVideoCallAppointmentMutation,
  useUpdateVideoCallAppointmentMutation,
  useCancelVideoCallAppointmentMutation,
  useJoinVideoCallAppointmentMutation,
  useGetSpecialistVideoCallAppointmentsQuery,
  useGetCurrentProgramQuery,
  useChangePatientProgramMutation,
  useGetPatientExerciseContentQuery,
  useGetPatientAiDayQuery,
  useGeneratePatientAiDayMutation,
  useRegeneratePatientAiDayMutation,
} = patientsApi;
